import {useCallback, useMemo} from 'react';

import {GuideLevelSortField, SortOrder} from 'gql/types';

import {useSkillPathEntryStatusCountsQuery} from '../queries/SkillPaths.queries.generated';
import type {SkillPathStatusSegment} from '../types';
import type {SharedSkillPathFilters} from './skillPathEntryFilters';
import {buildEntryFilter, toClassroomIds} from './skillPathEntryFilters';

/**
 * The primary key, so the row nobody reads comes back index-ordered. Any explicit sort would do —
 * what matters is that one is sent, which keeps the read's `default_sort` (a value computed from
 * three lateral aggregates) off a query that only needs its count.
 */
const CHEAPEST_SORT = [{field: GuideLevelSortField.Id, order: SortOrder.Asc}];

export interface SkillPathStatusCountsFilters extends SharedSkillPathFilters {
  classroomId?: string;
}

interface Result {
  /** Undefined until the first response — the control omits the numbers rather than showing 0. */
  counts?: Record<SkillPathStatusSegment, number>;
  /**
   * New totals are in flight and `counts` may hold the previous filters'. False while a warm cache
   * revalidates, where the numbers on hand are already this filter's.
   */
  isLoading: boolean;
  /** Every status is empty under the current filters — the state that shows the onboarding pitch. */
  isEmptyInEveryStatus: boolean;
  hasError: boolean;
  refetch: () => void;
}

/**
 * Totals per status for the shared segmented control. Separate from the list query so paging or
 * re-sorting the visible status doesn't refetch the other two.
 */
export const useSkillPathStatusCounts = ({
  classroomId,
  ...shared
}: SkillPathStatusCountsFilters): Result => {
  const {search, subject, dueWithin, dueDateStart, dueDateEnd} = shared;

  const variables = useMemo(() => {
    const filters = {search, subject, dueWithin, dueDateStart, dueDateEnd};
    return {
      classroomIds: toClassroomIds(classroomId),
      sort: CHEAPEST_SORT,
      allFilter: buildEntryFilter(filters, 'all'),
      openFilter: buildEntryFilter(filters, 'open'),
      closedFilter: buildEntryFilter(filters, 'closed'),
      scheduledFilter: buildEntryFilter(filters, 'scheduled')
    };
  }, [classroomId, search, subject, dueWithin, dueDateStart, dueDateEnd]);

  const {data, previousData, loading, error, refetch} = useSkillPathEntryStatusCountsQuery({
    variables,
    fetchPolicy: 'cache-and-network'
  });

  // Falls back to the previous totals mid-fetch so the segmented control keeps its numbers rather
  // than emptying them on every filter change.
  const settled = data ?? previousData;
  const all = settled?.all?.count ?? 0;
  const open = settled?.open?.count ?? 0;
  const closed = settled?.closed?.count ?? 0;
  const scheduled = settled?.scheduled?.count ?? 0;
  const hasCounts = Boolean(settled);
  // Keyed on the numbers, not the response, so the control's labels only rebuild when a total moves.
  const counts = useMemo(
    () => (hasCounts ? {all, open, closed, scheduled} : undefined),
    [hasCounts, all, open, closed, scheduled]
  );

  const handleRefetch = useCallback(() => {
    void refetch().catch(() => {});
  }, [refetch]);

  return {
    counts,
    isLoading: loading && !data,
    // The All total, not the sum: a status-scoped date filter can empty all three while All still
    // matches. Guarded on `hasCounts`, or a pending `?? 0` pitches onboarding at a full account.
    isEmptyInEveryStatus: hasCounts && !error && all === 0,
    hasError: Boolean(error),
    refetch: handleRefetch
  };
};
